import { languageName } from '@/ui/words';
import { useState } from 'react';
import { failureOf, said } from '@/api/errors';
import { useSequenceAct, useSequences, type WireSequence } from '@/api/hooks';
import { useMeOrThrow } from '@/app/MeContext';
import { Button, Field, Kicker, Skeleton, TextInput } from '@/ui/primitives';
import { Table, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { Textarea } from '@/ui/fields';
import { SequenceReaders } from '@/validation/FollowThrough';
import { useWho } from '@/ui/who';
import { explainSequence } from './explain';

/**
 * First-touch templates outside a campaign (ADR-0112).
 *
 * The words every first email starts from when no campaign covers the
 * restaurant: written by anyone in the department, approved as written by
 * its lead or a superadmin, and retired rather than edited. The agent
 * writes only the `{{paragraph}}`, and the draft still waits in the box for
 * a person to send — approving one lets nothing leave on its own.
 */

// How its readers found it (0155): a draft is approved once nobody's reading is open and the newest says it reads well, or nobody was asked.
const READ: Record<NonNullable<WireSequence['read']>, string> = {
  unread: 'Nobody asked',
  open: 'Being read',
  reads_well: 'Reads well',
  needs_changes: 'Needs changes',
};

const CHIP: Record<WireSequence['state'], { label: string; tone: 'warning' | 'accent' | 'neutral' }> = {
  draft: { label: 'WAITING FOR APPROVAL', tone: 'warning' },
  approved: { label: 'IN USE', tone: 'accent' },
  retired: { label: 'RETIRED', tone: 'neutral' },
};

export function Sequences() {
  const me = useMeOrThrow();
  const list = useSequences(me.department);
  const act = useSequenceAct();
  const toast = useToast();
  const who = useWho();
  const mayApprove = me.isSuperadmin || me.leads.includes(me.department);
  const [name, setName] = useState('');
  // Nothing is filled for a market the person did not choose; the placeholders show the shape.
  const [lang, setLang] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [words, setWords] = useState(80);
  const [error, setError] = useState<string | null>(null);
  const done = (text: string) => ({ onSuccess: () => toast({ text }), onError: (e: unknown) => setError(said(failureOf(e))) });
  // What pressing a template's row does: approve one waiting, retire one in use.
  const decide = (s: WireSequence) => {
    setError(null);
    if (s.state === 'draft') act.mutate({ act: 'approve', sequenceId: s.id }, done(`Approved. First emails in ${languageName(s.lang)} start from “${s.name}”.`));
    else if (s.state === 'approved') act.mutate({ act: 'retire', sequenceId: s.id }, done(`Retired. “${s.name}” is no longer used.`));
  };

  return (
    <section className="flex flex-col gap-3" aria-label="First-touch templates" data-testid="sequences">
      <Kicker>First emails outside a campaign</Kicker>
      <p className="m-0 text-dense text-secondary">
        Where no campaign covers a restaurant, its first email starts from the newest approved template in its language. The agent writes the one paragraph
        marked <code>{'{{paragraph}}'}</code>; the rest are these words, as approved. It still waits in the box for a person to send.
      </p>
      {list.isPending && <Skeleton rows={3} />}
      {list.isError && <Refusal failure={failureOf(list.error)} compact />}
      {list.data && (
        <Table<WireSequence>
          title="templates"
          rows={list.data.sequences}
          explain={(s) => explainSequence(s, who, { department: me.department, mayDecide: mayApprove, ...(mayApprove && s.state !== 'retired' ? { act: () => decide(s) } : {}) })}
          testId="sequence-list"
          emptyTitle="No templates yet"
          emptyBody="Until one is approved, a first email outside a campaign is the agent’s whole draft, for a person to read."
          columns={[
            { label: 'Template', get: (s) => s.name, primary: true },
            { label: 'Language', get: (s) => languageName(s.lang) },
            { label: 'Subject', get: (s) => s.templateSubject },
            { label: 'Read by a native reader', get: (s) => READ[s.read ?? 'unread'] },
            { label: 'Approved', get: (s) => (s.approvedBy ? `${s.approvedBy}, ${when(s.approvedAt!)}` : null) },
            { label: 'State', get: (s) => CHIP[s.state].label, chip: (s) => CHIP[s.state] },
          ]}
          {...(mayApprove
            ? {
                onOpen: decide,
              }
            : {})}
        />
      )}
      {mayApprove && <p className="m-0 text-dense text-meta">Open a template waiting for approval to approve it as written; open one in use to retire it.</p>}
      {list.data && list.data.sequences.some((s) => s.state === 'draft') && (
        <section className="flex flex-col gap-1" aria-label="Who reads them">
          <Kicker>Who reads them first</Kicker>
          <p className="m-0 text-dense text-secondary">
            Ask a colleague who reads the language natively. While they are reading, or if they say it needs changes, it is not approved; a changed one is written again.
          </p>
          {list.data.sequences
            .filter((s) => s.state === 'draft')
            .map((s) => (
              <SequenceReaders key={s.id} sequenceId={s.id} name={s.name} draft />
            ))}
        </section>
      )}
      <form
        className="flex flex-col gap-3"
        data-testid="sequence-write"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          act.mutate(
            { act: 'create', name: name.trim(), lang: lang.trim(), templateSubject: subject.trim(), templateBody: body, paragraphWords: words },
            { onSuccess: () => { toast({ text: 'Written. The lead approves it before it is used.' }); setName(''); setSubject(''); }, onError: (err) => setError(said(failureOf(err))) },
          );
        }}
      >
        <div className="flex flex-wrap gap-4">
          <Field label="Name">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} aria-label="Template name" placeholder="Swedish kitchens" />
          </Field>
          <Field label="Language" hint="Two letters, as the markets file names them.">
            <TextInput value={lang} onChange={(e) => setLang(e.target.value)} aria-label="Language" maxLength={2} placeholder="sv" />
          </Field>
          <Field label="Paragraph, at most" hint="20 to 120 words.">
            <TextInput type="number" min={20} max={120} value={words} onChange={(e) => setWords(Number(e.target.value))} aria-label="Paragraph words" />
          </Field>
        </div>
        <Field label="Subject">
          <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} aria-label="Template subject" maxLength={200} />
        </Field>
        <Field label="The email" hint="Exactly one {{paragraph}}. No links, nothing else in braces. The footer is added for you.">
          <Textarea aria-label="Template body" className="min-h-[120px]" value={body} onChange={(e) => setBody(e.target.value)} placeholder={'Hej,\n\n{{paragraph}}\n\nVill ni ha ett prov?'} />
        </Field>
        {error && <p className="m-0 text-dense text-danger">{error}</p>}
        <div>
          <Button type="submit" disabled={act.isPending || !name.trim() || !lang.trim() || !subject.trim() || !body.trim()}>Write it</Button>
        </div>
      </form>
    </section>
  );
}
